import type { ReactNode } from "react";
import { formatDueDate, isOverdue, statusLabel, type TaskSummary } from "../../app/hooks/useTasks.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { statusDotTone } from "./taskStatusDot.js";
import type { TaskPeople } from "./taskPeople.js";
import styles from "./TaskListRow.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

/**
 * THE APP'S TASK ROW — the one rendering of "a task in a list you can click into", shared by the dashboard
 * rail's To-do card and the releases hub's Release prep panel.
 *
 * The anatomy is the dashboard's, operator ruling D1 (2026-08-23): TASK | DUE, two columns — the task's name
 * over its sub-line, then the assigner → assignee faces stacked over the due date. <b>Status is carried by
 * the OVERDUE treatment alone, not a pill.</b> The four-column spreadsheet (TASK · DUE · PROGRESS · ASSIGNER ·
 * ASSIGNEE) belongs to the wide roster widget; at half-card width those fixed columns are what force every
 * title to ellipsize.
 *
 * <b>It is shared because the alternative is a fourth private copy.</b> The prep panel had one — a flat
 * sunken pill carrying a bare title, a 10px due stamp and a `StatusPill`, with no faces at all — so the hub
 * and the dashboard rail described the same open task two different ways. Same reasoning as `TaskAssignees`
 * next door (ART-131): a row idiom that lives inside one caller's stylesheet is one the next caller re-types.
 *
 * <b>The caller still owns the LIST FRAME</b> — `taskRowsClassName` (in `taskRow.ts`) is the bare `<ul>`
 * reset; where it scrolls, whether it bleeds to the card's edges and what column heads sit above it are the
 * caller's, because those are decisions about the CARD rather than about the row.
 */
export function TaskListRow({
  task,
  people,
  sub,
  density,
  onOpen,
}: {
  task: TaskSummary;
  people: TaskPeople;
  /** The row's ONE sub-line — compose it with `taskSubLine`, which a caller may narrow. */
  sub?: string | null;
  /** `compact` — the same row a size down, for a narrow card (the roster's To-do, ART-333). */
  density?: "compact";
  /** Absent ⇒ the surface cannot open a task: the row renders as plain content, with no dead button. */
  onOpen?: () => void;
}) {
  const overdue = isOverdue(task.dueDate, task.status);
  // ART-343: a task that has MOVED (in progress, needs review, …) says so with a small STATUS CHIP in the row's
  // right cluster, in the drawer's own status colours, on EVERY task list (this is the one task row). An open task
  // draws none: open is what every new row is.
  //
  // DESKTOP: the chip, the faces, then the due date column; an overdue row also takes the crimson wash and rail.
  // PHONE (below lg): ONE chip before the faces and no date column (the mobile home's original To-Do look). The
  // chip says the most urgent thing: "Overdue", else the status, else the due date.
  const desktop = !usePhoneLayout();
  const tone = statusDotTone(task.status);
  const moved = tone !== "open";
  const chip: { label: string; tone: string } | null = desktop
    ? moved ? { label: statusLabel(task.status), tone } : null
    : overdue ? { label: "Overdue", tone: "overdue" }
      : moved ? { label: statusLabel(task.status), tone }
        : task.dueDate ? { label: formatDueDate(task.dueDate), tone: "due" } : null;
  return (
    <li
      className={styles.row}
      data-overdue={(desktop && overdue) || undefined}
      data-density={density}
    >
      <RowFrame onOpen={onOpen}>
        <span className={styles.colTask}>
          <span className={styles.taskName}>
            {task.title || "Untitled task"}
          </span>
          {sub && (
            <span className={styles.taskSub} title={sub}>
              {sub}
            </span>
          )}
        </span>
        {/* The right cluster runs on ONE line, right-aligned: the chip, the assigner→assignee faces, then (desktop)
            the due date. */}
        <span className={styles.colDue}>
          {chip && (
            <span className={styles.statusChip} data-tone={chip.tone}>
              {chip.label}
            </span>
          )}
          <RowPeople task={task} people={people} faceSize={density === "compact" ? "xs" : "sm"} />
          {desktop && <span className={styles.due}>{task.dueDate ? formatDueDate(task.dueDate) : "—"}</span>}
        </span>
      </RowFrame>
    </li>
  );
}

/** The row's click target, or — on a surface that cannot open a task — the same box with nothing to press. */
function RowFrame({ onOpen, children }: { onOpen?: () => void; children: ReactNode }) {
  return onOpen ? (
    <button type="button" className={styles.rowButton} onClick={onOpen}>
      {children}
    </button>
  ) : (
    <div className={styles.rowButton} data-static="">
      {children}
    </div>
  );
}

/**
 * The row's faces: who put this person on the task, then who is on it. The pair collapses to ONE face when
 * they are the same person (a self-assigned task rendering "AB → AB" is noise) or when nobody is assigned
 * yet — in which case the creator is who it currently sits with, which is the honest reading. Extra
 * assignees become a `+N` rather than a row of shrinking circles.
 *
 * The "from" face is the ASSIGNER (`assignedByPersonId`, recorded per assignment), not the task's
 * `initiatorPersonId` — which is its CREATOR. The two are the same person only when whoever wrote the task
 * also handed it out, so the row read "from" the creator for every task somebody else routed: a task
 * Whitsett wrote and Ryan then assigned announced itself as Ryan's work coming from Whitsett (ART-120).
 * The creator is still the fallback, for a row assigned before the column existed and for the unassigned
 * case above; both are genuinely "this sits with the person who wrote it".
 */
function RowPeople({ task, people, faceSize }: { task: TaskSummary; people: TaskPeople; faceSize: "xs" | "sm" }) {
  const first = task.assignees?.[0];
  const assignee = first?.personId ?? null;
  const assigner = first?.assignedByPersonId ?? task.initiatorPersonId ?? null;
  const extra = Math.max(0, (task.assignees?.length ?? 0) - 1);
  const pair = !!assignee && assignee !== assigner;

  const from = people.nameOf(assigner, "Someone");
  const to = people.nameOf(assignee, "Assignee");

  return (
    <span className={styles.people}>
      <Face personId={assigner} name={from} people={people} size={faceSize} />
      {pair && (
        <>
          <span className={styles.arrow} aria-hidden="true">
            →
          </span>
          <Face personId={assignee} name={to} people={people} size={faceSize} />
        </>
      )}
      {extra > 0 && <span className={styles.moreCount}>+{extra}</span>}
      {/* The faces carry no text of their own, so the row's accessible name would lose the people entirely. */}
      <span className={styles.srOnly}>{pair ? `${from} to ${to}` : from}</span>
    </span>
  );
}

function Face({ personId, name, people, size }: {
  personId: string | null;
  name: string;
  people: TaskPeople;
  size: "xs" | "sm";
}) {
  return (
    <PersonAvatar
      personId={personId}
      avatarMediaId={people.person(personId)?.avatarMediaId}
      // The person's OWN initials, never the display name's: `nameOf` says "You" for the caller, and initialling
      // that puts a "Y" on your own face. Measured in the running app, which is the only place it shows.
      initials={people.initialsOf(personId, name)}
      size={size}
      className={styles.face}
    />
  );
}
